/**
 * Transforms technical landmark names from mdsim_constants to user-friendly
 * anatomical terms for display in the UI.
 *
 * Naming convention from mdsim_constants:
 * - H = Head (Superior/Top endplate)
 * - F = Foot (Inferior/Bottom endplate)
 * - L/R = Left/Right (coronal view)
 * - A/P = Anterior/Posterior (sagittal view)
 * - C = Center
 *
 * Examples:
 *   "vertebrae_L5_HL" → "L5 Superior Left"
 *   "vertebrae_T12_pedicle_R" → "T12 Right Pedicle"
 *   "IVD_L4_L5" → "L4-L5 Disc"
 *   "sacrum_C" → "Sacrum Center"
 */

const POSITION_SUFFIXES = {
  HL: "Superior Left",
  HR: "Superior Right",
  FL: "Inferior Left",
  FR: "Inferior Right",
  HA: "Superior Anterior",
  HP: "Superior Posterior",
  FA: "Inferior Anterior",
  FP: "Inferior Posterior",
  C: "Center",
  HC: "Horizontal Center",
};

const SIDE_MAP = {
  L: "Left",
  R: "Right",
};

/**
 * Extracts the vertebra level from a name like "vertebrae_L5" or "vertebrae_T12"
 * @param {string} prefix - The vertebrae prefix (e.g., "vertebrae_L5")
 * @returns {string} - The level (e.g., "L5", "T12")
 */
const extractVertebraLevel = prefix => {
  const match = prefix.match(/vertebrae_([A-Z]\d+)/i);
  return match ? match[1].toUpperCase() : prefix.replace("vertebrae_", "");
};

/**
 * Formats an IVD (intervertebral disc) name
 * @param {string} name - e.g., "IVD_L4_L5" or "IVD_L5_SACRUM"
 * @returns {string} - e.g., "L4-L5 Disc" or "L5-Sacrum Disc"
 */
const formatIVDName = name => {
  const withoutPrefix = name.replace(/^IVD_/i, "");
  const parts = withoutPrefix.split("_");

  if (parts.length >= 2) {
    const level1 = parts[0].toUpperCase();
    const level2 =
      parts[1].toUpperCase() === "SACRUM"
        ? "Sacrum"
        : parts.slice(1).join("").toUpperCase();
    return `${level1}-${level2} Disc`;
  }

  return name;
};

/**
 * Formats a pedicle landmark name
 * @param {string} name - e.g., "vertebrae_L5_pedicle_L" or "sacrum_pedicle_R"
 * @returns {string} - e.g., "L5 Left Pedicle" or "Sacrum Right Pedicle"
 */
const formatPedicleName = name => {
  const parts = name.split("_pedicle_");
  if (parts.length !== 2) return name;

  const [prefix, side] = parts;
  const sideName = SIDE_MAP[side.toUpperCase()] || side;

  if (prefix.startsWith("vertebrae_")) {
    const level = extractVertebraLevel(prefix);
    return `${level} ${sideName} Pedicle`;
  }

  if (prefix === "sacrum") {
    return `Sacrum ${sideName} Pedicle`;
  }

  return name;
};

/**
 * Formats an S2 alar-iliac landmark name
 * @param {string} name - e.g., "S2_alar_iliac_L"
 * @returns {string} - e.g., "S2 Alar-Iliac Left"
 */
const formatAlarIliacName = name => {
  const match = name.match(/S2_alar_iliac_([LR])/i);
  if (match) {
    const side = SIDE_MAP[match[1].toUpperCase()];
    return `S2 Alar-Iliac ${side}`;
  }
  return name;
};

/**
 * Formats a standard vertebra or sacrum landmark name with position suffix
 * @param {string} name - e.g., "vertebrae_L5_HL" or "sacrum_C"
 * @returns {string} - e.g., "L5 Superior Left" or "Sacrum Center"
 */
const formatStandardLandmark = name => {
  // Handle sacrum landmarks
  if (name.startsWith("sacrum_")) {
    const suffix = name.replace("sacrum_", "").toUpperCase();
    const position = POSITION_SUFFIXES[suffix];
    if (position) {
      return `Sacrum ${position}`;
    }
    return name;
  }

  // Handle vertebrae landmarks
  if (name.startsWith("vertebrae_")) {
    // Special case: vertebrae_L_Apex
    if (name === "vertebrae_L_Apex") {
      return "Lumbar Apex";
    }

    // Parse vertebrae_XX_YY pattern
    const match = name.match(/vertebrae_([A-Z]\d+)_(\w+)/i);
    if (match) {
      const level = match[1].toUpperCase();
      const suffix = match[2].toUpperCase();
      const position = POSITION_SUFFIXES[suffix];

      if (position) {
        return `${level} ${position}`;
      }
    }

    // Fallback: just extract the level
    const levelMatch = name.match(/vertebrae_([A-Z]\d+)/i);
    if (levelMatch) {
      return levelMatch[1].toUpperCase();
    }
  }

  return name;
};

/**
 * Transforms a technical landmark name to a user-friendly anatomical term.
 *
 * @param {string} name - The technical landmark name from mdsim_constants
 * @returns {string} - The user-friendly display name
 */
export const formatLandmarkName = name => {
  if (!name || typeof name !== "string") {
    return name || "";
  }

  // Handle IVD (intervertebral disc) names
  if (name.toUpperCase().startsWith("IVD_")) {
    return formatIVDName(name);
  }

  // Handle pedicle landmarks
  if (name.includes("_pedicle_")) {
    return formatPedicleName(name);
  }

  // Handle S2 alar-iliac landmarks
  if (name.toLowerCase().startsWith("s2_alar_iliac_")) {
    return formatAlarIliacName(name);
  }

  // Handle standard vertebra/sacrum landmarks with position suffixes
  return formatStandardLandmark(name);
};

export default formatLandmarkName;
